import { Controller, useForm } from "react-hook-form";
import {
  InputLabel,
  Text,
  TextInput,
  Textarea,
  Grid,
  Select,
} from "@mantine/core";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { InputMaybe, Scalars } from "@/__generated__/graphql";
import styles from "./refer-patient.module.scss";
import states from "@/data/states.json";
import {
  getConditionsTreatedByMedication,
  Medication,
} from "@/data/medication-condition-pairs";
import { DenialReason } from "@/data/denial-reasons";

export type ReferralFormInput = {
  firstName?: InputMaybe<Scalars["String"]["input"]>;
  lastName?: InputMaybe<Scalars["String"]["input"]>;
  patientEmail?: InputMaybe<Scalars["String"]["input"]>;
  dateOfBirth?: InputMaybe<Scalars["String"]["input"]>;
  condition?: InputMaybe<Scalars["String"]["input"]>;
  state?: InputMaybe<Scalars["String"]["input"]>;
  medication?: InputMaybe<Scalars["String"]["input"]>;
  denialReason?: InputMaybe<Scalars["String"]["input"]>;
  message?: InputMaybe<Scalars["String"]["input"]>;
};

type ReferralFormProps = {
  formId: string;
  onSubmit: (formData: ReferralFormInput) => Promise<void>;
};

const schema = z.object({
  firstName: z.string().min(1, { message: "First name is required" }),
  lastName: z.string().min(1, { message: "Last name is required" }),
  patientEmail: z
    .string({ message: "Email is required" })
    .email({ message: "Invalid email" }),
  dateOfBirth: z.string({ message: "Date of birth is required" }),
  state: z.string({ message: "State is required" }),
  condition: z.string({ message: "Condition is required" }),
  medication: z.string({ message: "Medication is required" }),
  denialReason: z.string({ message: "Denial Reason is required" }),
  message: z.string({ message: "A message is required" }),
});

function ReferralForm({ formId, onSubmit }: ReferralFormProps) {
  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ReferralFormInput>({
    defaultValues: {
      firstName: undefined,
      lastName: undefined,
      patientEmail: undefined,
      dateOfBirth: undefined,
      state: undefined,
      condition: undefined,
      medication: undefined,
      denialReason: undefined,
      message: undefined,
    },
    resolver: zodResolver(schema),
  });

  const medication = watch("medication");

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)}>
      <Grid>
        <Grid.Col>
          <Controller
            name="medication"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                label="Medication"
                data-testid="medication-input"
                searchable
                error={errors.medication?.message}
                data={Object.values(Medication).sort()}
              />
            )}
          />
        </Grid.Col>
      </Grid>
      <Grid>
        <Grid.Col>
          <Controller
            name="condition"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                label="Condition"
                data-testid="condition-input"
                searchable
                error={errors.condition?.message}
                disabled={!medication}
                data={
                  medication
                    ? [
                        ...getConditionsTreatedByMedication(
                          medication as Medication,
                        ),
                      ]
                    : [""]
                }
              />
            )}
          />
        </Grid.Col>
      </Grid>
      <Grid>
        <Grid.Col>
          <Controller
            name="denialReason"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                label="Denial Reason"
                data-testid="denial-reason-input"
                searchable
                error={errors.denialReason?.message}
                data={Object.values(DenialReason).sort()}
              />
            )}
          />
        </Grid.Col>
      </Grid>
      <Grid>
        <Grid.Col>
          <TextInput
            {...register("firstName")}
            label="First Name"
            data-testid="patient-first-name-input"
            error={errors.firstName?.message}
          />
        </Grid.Col>
      </Grid>
      <Grid>
        <Grid.Col>
          <TextInput
            {...register("lastName")}
            data-testid="patient-last-name-input"
            label="Last Name"
            error={errors.lastName?.message}
          />
        </Grid.Col>
      </Grid>
      <Grid>
        <Grid.Col>
          <TextInput
            {...register("patientEmail")}
            label="Patient Email"
            data-testid="patient-email-input"
            error={errors.patientEmail?.message}
          />
          <Text className="px-2" fz="sm" c="gray" ta="left" mb="lg">
            {
              "Email will link to the patient’s account. Please ensure it matches."
            }
          </Text>
        </Grid.Col>
      </Grid>
      <Grid>
        <Grid.Col>
          <TextInput
            {...register("dateOfBirth")}
            data-testid="patient-birth-date-input"
            type="date"
            label="Birth Date"
            error={errors.dateOfBirth?.message}
          />
        </Grid.Col>
        <Grid.Col>
          <Controller
            name="state"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                label="State"
                data-testid="patient-state-input"
                searchable
                error={errors.state?.message}
                data={states.map(({ state, stateCode }) => ({
                  value: stateCode,
                  label: state,
                }))}
                classNames={{ input: styles.input, label: styles.inputLabel }}
              />
            )}
          />
        </Grid.Col>
      </Grid>
      <Grid mt="md">
        <Grid.Col>
          <InputLabel htmlFor="message">
            <Text fz="xl" fw="500" className="textarea_label">
              {"Add a personal message for your patient"}
            </Text>
            <Text fz="sm" c="gray" ta="left" mb="lg">
              {
                "Privacy matters – please don’t share protected health info (PHI) here."
              }
            </Text>
            <Textarea
              {...register("message")}
              placeholder="We’ve teamed up with Claimable to help fight insurance denials. Good news—we’ve already started an appeal for you! If you need any support along the way, just reach out to Claimable at support@getclaimable.com. They’re here to help!"
              resize="vertical"
              size="xl"
              radius="lg"
              fz="md"
              data-testid="provider-message-input"
              error={errors.message?.message}
            />
          </InputLabel>
        </Grid.Col>
      </Grid>
    </form>
  );
}

export default ReferralForm;
